import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, FileText, Clock, Truck, Wallet, PackageSearch } from "lucide-react"
import { Page, Button } from "./ui"
import {
  FilterBar,
  DataTable,
  StatusPill,
  MoneyText,
  DateText,
  KpiTile,
  KpiStrip,
  EmptyState,
  ProgressDots,
  en,
} from "procurement/design"
import useProcurementFilters from "./hooks/useProcurementFilters"
import useProcurementListFilters from "./hooks/useProcurementListFilters"
import {
  useGetPurchasesQuery,
  useGetPurchaseSuppliersQuery,
} from "shared/redux/endpoints/procurementPurchases"
import { PO_STATUS, PO_STATUS_LABEL } from "./status"
import PurchaseQuickDrawer from "./components/purchase/PurchaseQuickDrawer"
import OrderRowMenu from "./components/orders/OrderRowMenu"

const STATUS_OPTIONS = Object.entries(PO_STATUS_LABEL).map(([value, label]) => ({ value, label }))
const FILTER_DEFAULTS = {
  search: "",
  category: "",
  status: "",
  region: "",
  center: "",
  department: "",
  item: "",
  fromDate: "",
  toDate: "",
  supplier: "",
  awaiting: "",
  density: "comfortable",
  kpi: "",
}

// KPI tiles filter client-side so switching tiles doesn't refetch and zero out
// the other tiles' counts. Each predicate mirrors how its tile is counted.
const KPI_FILTERS = {
  open: (r) => [PO_STATUS.ISSUED, PO_STATUS.RECEIVING, PO_STATUS.APPROVED, PO_STATUS.PENDING].includes(r.status),
  awaiting: (r) => r.status === PO_STATUS.PENDING,
  receiving: (r) => r.status === PO_STATUS.RECEIVING,
  unpaid: (r) => !r.paidStatus || r.paidStatus === "unpaid" || r.paidStatus === "partial",
}

const PO_STEPS = [
  { key: "issued", label: en.purchases.issued },
  { key: "receiving", label: en.purchases.receiving },
  { key: "completed", label: en.purchases.completed },
]

const paidLabel = (v) => {
  if (!v) return null
  if (v === "paid") return "Paid"
  if (v === "partial") return "Partial"
  return "Unpaid"
}

const PurchasesContainer = ({ approvalOnly = false, title }) => {
  const navigate = useNavigate()
  const { filters, setFilters, resetFilters } = useProcurementFilters(FILTER_DEFAULTS)
  const awaiting = approvalOnly || filters.awaiting === "1"
  const [drawerId, setDrawerId] = useState(null)

  const { categoryOptions, regionOptions, centerOptions, departmentOptions, itemOptions, itemsLoading, queryFilters } =
    useProcurementListFilters(filters)

  const { data, isFetching } = useGetPurchasesQuery({
    ...queryFilters,
    status: filters.status || undefined,
    supplier: filters.supplier || undefined,
    awaitingMe: awaiting || undefined,
  })
  const { data: suppliers } = useGetPurchaseSuppliersQuery()

  const supplierOptions = useMemo(
    () => (suppliers || []).map((s) => ({ value: String(s.id), label: s.name })),
    [suppliers]
  )

  const rows = useMemo(() => {
    const items = (data?.items || []).map((p) => ({
      id: p.id ?? p.poCode,
      poCode: p.poCode,
      created: p.createdAt,
      region: p.region,
      category: p.budgetCategoryName || p.categoryName,
      supplier: p.internalTransfer ? `Internal · ${p.vendorName || p.supplierName || ""}` : p.vendorName || p.supplierName,
      total: p.totalAmount ?? p.total,
      status: p.status,
      reason: p.reason,
      paidStatus: p.sunPaymentStatus || p.paidStatus,
    }))
    const q = filters.search.trim().toLowerCase()
    return q
      ? items.filter((r) => `${r.poCode} ${r.supplier} ${r.category}`.toLowerCase().includes(q))
      : items
  }, [data, filters.search])

  const openCount = rows.filter(KPI_FILTERS.open).length
  const receivingCount = rows.filter(KPI_FILTERS.receiving).length
  const awaitingCount = rows.filter(KPI_FILTERS.awaiting).length
  const unpaidValue = rows.filter(KPI_FILTERS.unpaid).reduce((s, r) => s + (Number(r.total) || 0), 0)

  const kpiFilter = KPI_FILTERS[filters.kpi]
  const visibleRows = useMemo(() => (kpiFilter ? rows.filter(kpiFilter) : rows), [rows, kpiFilter])
  const toggleKpi = (key) => setFilters({ kpi: filters.kpi === key ? "" : key })

  const columns = [
    { key: "poCode", header: en.purchases.poCode, strong: true, sortable: true },
    {
      key: "supplier",
      header: en.purchases.supplier,
      className: "pc-col-title",
      render: (r) => (
        <span title={r.supplier}>
          <span className="pc-cell-title">{r.supplier || "—"}</span>
          {r.category && <div className="pc-cell-sub">{r.category}</div>}
        </span>
      ),
    },
    { key: "region", header: en.purchases.region, className: "pc-col-nowrap", render: (r) => r.region || "—" },
    {
      key: "total",
      header: en.purchases.total,
      align: "right",
      sortable: true,
      value: (r) => r.total,
      className: "pc-col-total",
      render: (r) => <MoneyText value={r.total} />,
    },
    {
      key: "status",
      header: en.purchases.status,
      className: "pc-col-status",
      render: (r) => (
        <span className="pc-stack">
          <StatusPill status={r.status} />
          {r.paidStatus && <StatusPill status={r.paidStatus} label={paidLabel(r.paidStatus)} />}
          {r.reason && (
            <span className="pc-cell-sub pc-cell-reason" title={r.reason}>
              {r.reason}
            </span>
          )}
        </span>
      ),
    },
    {
      key: "progress",
      header: en.purchases.progress,
      render: (r) => <ProgressDots steps={PO_STEPS} current={r.status} />,
    },
    { key: "created", header: en.purchases.created, sortable: true, className: "pc-col-nowrap", render: (r) => <DateText value={r.created} /> },
    {
      key: "actions",
      header: en.purchases.actions,
      className: "pc-col-actions",
      render: (r) => (
        <span className="pc-row-actions">
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              setDrawerId(r.id)
            }}
          >
            View
          </Button>
          <OrderRowMenu
            items={[
              { key: "full", label: en.common.openFullPage, onClick: () => navigate(`/procurement/purchases/${r.id}`) },
            ]}
          />
        </span>
      ),
    },
  ]

  const heading = approvalOnly ? en.purchases.awaitingApproval : title || en.purchases.title

  return (
    <Page
      className="pc-page--refined pc-page--list"
      breadcrumb={[
        "Procurement",
        ...(heading === en.purchases.title ? [] : [{ label: en.purchases.title, to: "/procurement/purchases" }]),
        heading,
      ]}
      title={heading}
      subtitle={approvalOnly ? en.purchases.approvalSubtitle : en.purchases.subtitle}
      actions={
        !approvalOnly && (
          <Button variant="primary" leftIcon={<Plus aria-hidden />} onClick={() => navigate("/procurement/purchases/new")}>
            {en.purchases.createPo}
          </Button>
        )
      }
    >
      <KpiStrip>
        <KpiTile
          icon={<FileText aria-hidden />}
          label={en.purchases.openPos}
          value={isFetching ? "—" : openCount}
          iconTone="info"
          active={filters.kpi === "open"}
          onClick={() => toggleKpi("open")}
        />
        <KpiTile
          icon={<Clock aria-hidden />}
          label={en.purchases.awaitingKpi}
          value={isFetching ? "—" : awaitingCount}
          iconTone="warning"
          active={filters.kpi === "awaiting"}
          onClick={() => toggleKpi("awaiting")}
        />
        <KpiTile
          icon={<Truck aria-hidden />}
          label={en.purchases.receiving}
          value={isFetching ? "—" : receivingCount}
          iconTone="info"
          active={filters.kpi === "receiving"}
          onClick={() => toggleKpi("receiving")}
        />
        <KpiTile
          icon={<Wallet aria-hidden />}
          label={en.purchases.unpaidValue}
          value={<MoneyText value={unpaidValue} compact align="left" />}
          iconTone="danger"
          active={filters.kpi === "unpaid"}
          onClick={() => toggleKpi("unpaid")}
        />
      </KpiStrip>

      <FilterBar
        title={en.filter.titlePurchases}
        values={filters}
        onChange={setFilters}
        onReset={resetFilters}
        fields={[
          { key: "search", label: en.filter.search, placeholder: en.filter.searchPo },
          ...(approvalOnly
            ? []
            : [
                {
                  key: "awaiting",
                  label: en.filter.view,
                  type: "select",
                  allowEmpty: false,
                  options: [
                    { value: "", label: en.purchases.all },
                    { value: "1", label: en.purchases.awaitingApproval },
                  ],
                },
              ]),
          { key: "region", label: en.filter.region, type: "select", options: regionOptions },
          { key: "center", label: en.filter.center, type: "select", options: centerOptions, placeholder: en.filter.allCenters },
          { key: "department", label: en.filter.department, type: "select", options: departmentOptions, placeholder: en.filter.allDepartments },
          { key: "category", label: en.filter.category, type: "select", options: categoryOptions, placeholder: en.filter.allCategories },
          { key: "supplier", label: en.filter.supplier, type: "select", options: supplierOptions, placeholder: en.filter.allSuppliers },
          { key: "status", label: en.filter.status, type: "select", options: STATUS_OPTIONS, placeholder: en.filter.allStatuses },
          { key: "item", label: en.filter.item, type: "multiselect", options: itemOptions, loading: itemsLoading, placeholder: en.filter.allItems, searchPlaceholder: en.filter.searchItem },
          { key: "fromDate", label: en.filter.createdFrom, type: "date" },
          { key: "toDate", label: en.filter.createdTo, type: "date" },
        ]}
      />
      {filters.fromDate && filters.toDate && filters.toDate < filters.fromDate && (
        <p className="pc-hint" style={{ margin: "-8px 0 12px", color: "var(--pc-danger)" }}>
          {en.filter.invalidDateRange}
        </p>
      )}

      <div className="ds-table-toolbar is-attached">
        <span>{en.common.results(visibleRows.length)}</span>
        <div className="ds-table-toolbar-right">
          <select
            className="pc-control"
            style={{ width: "auto", padding: "6px 28px 6px 10px" }}
            value={filters.density}
            onChange={(e) => setFilters({ density: e.target.value })}
            aria-label={en.common.density}
          >
            <option value="comfortable">{en.common.comfortable}</option>
            <option value="compact">{en.common.compact}</option>
          </select>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={visibleRows}
        isLoading={isFetching}
        density={filters.density}
        onRowClick={(r) => setDrawerId(r.id)}
        empty={
          <EmptyState
            icon={<PackageSearch aria-hidden />}
            title={awaiting ? en.purchases.emptyAwaiting : en.purchases.emptyTitle}
            text={awaiting ? en.purchases.emptyAwaitingText : en.purchases.emptyText}
            action={
              <Button variant="secondary" size="sm" onClick={resetFilters}>
                {en.common.clearFilters}
              </Button>
            }
          />
        }
      />

      <PurchaseQuickDrawer purchaseId={drawerId} open={!!drawerId} onClose={() => setDrawerId(null)} />
    </Page>
  )
}

export default PurchasesContainer
